// Combos / packages — thin wrappers over the P1 backend
// (20260604000010_combos_schema.sql + 20260604000020_combo_pricing_and_holds.sql).
//
// Reads ride PostgREST directly: the combos/combo_components RLS policies
// expose only published+enabled rows to anon, so the widget needs no
// get_widget_config bump. Pricing + holds go through the security-definer
// RPCs, which re-check conditions server-side — everything here is a
// preview/UX layer, never the source of truth.

import { supabase } from "../config";
import type { WidgetCombo } from "../types";

/**
 * Published, bookable combos for a tenant.
 *
 * `failed` distinguishes "this tenant has no combos" from "we could not read
 * them". Both used to return an empty array and hide the Packages tab with
 * nothing logged, so a missing grant, an RLS change or an unapplied migration
 * looked exactly like a tenant who had never created a package — on a surface
 * that sells things. Diagnosing one such report meant reading the resolver,
 * the widget config and four gate conditions before the answer turned out to
 * be "it was fine all along".
 */
export async function fetchPublishedCombos(
  tenantId: string,
): Promise<{ combos: WidgetCombo[]; failed: boolean }> {
  const { data, error } = await supabase
    .from("combos")
    .select(
      "id, slug, name, description, hero_image_url, display_order, brand_id, " +
        "pricing_kind, discount_percent, fixed_price_cents, buy_qty, pay_qty, " +
        "days_of_week, min_party_size, max_party_size, min_nights, " +
        "sales_starts_on, sales_ends_on, currency, " +
        "combo_components ( id, service_id, service_unit_id, quantity, is_quantity_driver, display_order )",
    )
    .eq("tenant_id", tenantId)
    .eq("status", "published")
    .eq("is_enabled", true)
    .order("display_order", { ascending: true });
  if (error || !data) {
    // Loud on purpose. A silent empty list here is indistinguishable from a
    // correctly-empty one, and the guest-facing consequence is a tab that
    // simply is not there.
    console.error(
      "[vertu-widget] could not load combos — the Packages tab will not render:",
      error?.message ?? "no rows returned",
    );
    return { combos: [], failed: true };
  }
  // Drop combos whose window has CLOSED. Deliberately not the other half:
  // sales_starts_on / sales_ends_on are enforced by the server against the
  // ARRIVAL date, not the browsing date, so an offer that runs in September is
  // perfectly bookable in August — and filtering it out here made it invisible
  // to exactly the guest who would have taken it. The panel says which stays
  // an offer covers; the server still refuses a date outside the window.
  const today = new Date().toISOString().slice(0, 10);
  const combos = (data as unknown as WidgetCombo[])
    .filter((c) => c.sales_ends_on == null || c.sales_ends_on >= today)
    .map((c) => ({
      ...c,
      combo_components: [...(c.combo_components ?? [])].sort(
        (a, b) => a.display_order - b.display_order,
      ),
    }));
  return { combos, failed: false };
}

// One configured component of a combo, ready to price/hold. Mirrors the
// full selection shape check_combo_availability / create_combo_hold accept —
// including the optional dining seat/area fields the server forwards to
// create_booking_hold (no picker UI yet, but the contract is plumbed).
export type ComboSelection = {
  service_id: string;
  service_unit_id: string | null;
  /** True when the chosen unit has NO base_price — the hold prices it via
   *  deposit rules, so the estimate must too (set by CombosPanel, which has
   *  the unit rows). */
  unit_unpriced?: boolean;
  starts_at: string;
  ends_at: string;
  party_size: number;
  floor_plan_id?: string | null;
  floor_plan_element_id?: string | null;
  seating_area_id?: string | null;
};

type PriceAllocation = { ref: string; amount_cents: number };

export type ComboPriceEstimate =
  | {
      ok: true;
      base_total_cents: number;
      bundle_total_cents: number;
      discount_cents: number;
      allocations: PriceAllocation[];
    }
  | { ok: false; reason: string; detail?: string };

// Client-side price preview. Builds the same per-component payload
// create_combo_hold assembles server-side: rules-aware list price via
// resolve_unit_price for unit components (0 for unit-less ones — their
// deposit is resolved at hold time), quantity = fixed component quantity
// or nights derived from the window, and the buy-X-pay-Y driver flag.
export async function estimateComboPrice(
  combo: WidgetCombo,
  selections: ComboSelection[],
): Promise<ComboPriceEstimate> {
  const components: Record<string, unknown>[] = [];
  for (let i = 0; i < selections.length; i++) {
    const sel = selections[i];
    if (!sel) continue;
    const comp = combo.combo_components.find(
      (c) => c.service_id === sel.service_id,
    );
    let listCents = 0;
    let nights = 0;
    if (sel.service_unit_id && !sel.unit_unpriced) {
      const { data } = await supabase.rpc("resolve_unit_price", {
        p_service_unit_id: sel.service_unit_id,
        p_starts_at: sel.starts_at,
        p_ends_at: sel.ends_at,
        p_party_size: sel.party_size,
      });
      const priced = data as {
        effective_cents?: number;
        nights?: number;
      } | null;
      nights = priced?.nights ?? 0;
      // effective_cents is the NIGHTLY/base rate — the canonical hold
      // multiplies by nights, and create_combo_hold (20260712000040) now
      // uses the hold's amount as the list price, so the preview matches.
      listCents = (priced?.effective_cents ?? 0) * Math.max(1, nights);
    } else {
      nights = Math.max(
        0,
        Math.round(
          (new Date(sel.ends_at).setHours(0, 0, 0, 0) -
            new Date(sel.starts_at).setHours(0, 0, 0, 0)) /
            86_400_000,
        ),
      );
      // Estimate/hold parity (review finding): the hold prices BOTH unit-less
      // legs AND null-base_price units via resolve_deposit_cents, but the
      // preview said 0 — the guest saw one total and was charged a higher one
      // (or, with no deposit rule, the leg genuinely holds at 0 and the pill
      // now says so honestly). Same RPC, tables_count 0, matching the hold's
      // unit-hold path. Anon-granted since 20260723000020.
      const { data: dep } = await supabase.rpc("resolve_deposit_cents", {
        p_service_id: sel.service_id,
        p_party_size: sel.party_size,
        p_tables_count: 0,
      });
      listCents = typeof dep === "number" && dep > 0 ? dep : 0;
    }
    components.push({
      ref: String(i),
      service_id: sel.service_id,
      amount_cents: listCents,
      // The actual window drives the price; the component's pinned quantity is
      // only a ceiling (create_combo_hold rejects longer, allows shorter).
      // Sending the pin made the preview disagree with the hold on any shorter
      // stay.
      quantity: nights > 0 ? nights : comp?.quantity ?? null,
      is_driver: comp?.is_quantity_driver ?? false,
    });
  }

  const partyMax = Math.max(1, ...selections.map((s) => s.party_size));
  const when = selections
    .map((s) => s.starts_at)
    .sort()[0];

  const { data, error } = await supabase.rpc("resolve_combo_price", {
    p_combo_id: combo.id,
    p_components: components,
    p_party_size: partyMax,
    p_when: when,
  });
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return data as ComboPriceEstimate;
}

export async function checkComboAvailability(
  comboId: string,
  selections: ComboSelection[],
): Promise<{ ok: true } | { ok: false; reason: string; detail?: unknown }> {
  const { data, error } = await supabase.rpc("check_combo_availability", {
    p_combo_id: comboId,
    p_selections: selections,
  });
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return data as { ok: true } | { ok: false; reason: string };
}

export type ComboHoldResult =
  | {
      ok: true;
      combo_id: string;
      booking_ids: string[];
      base_total_cents: number;
      bundle_total_cents: number;
      discount_cents: number;
      allocations: PriceAllocation[]; // ref = component booking id
    }
  | { ok: false; reason: string; detail?: unknown };

export async function createComboHold(input: {
  comboId: string;
  selections: ComboSelection[];
  cartToken: string;
  holdMinutes?: number;
}): Promise<ComboHoldResult> {
  const { data, error } = await supabase.rpc("create_combo_hold", {
    p_combo_id: input.comboId,
    p_selections: input.selections,
    p_cart_token: input.cartToken,
    p_hold_minutes: input.holdMinutes ?? 15,
  });
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return data as ComboHoldResult;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "25% off" / "R 3 500 package" / "Book 3 · pay 2" / "Bundle" */
export function comboPricingLabel(combo: WidgetCombo): string {
  switch (combo.pricing_kind) {
    case "percent_off":
      return `${stripTrailingZeros(combo.discount_percent ?? 0)}% off`;
    case "fixed_price":
      return `${currencySymbol(combo.currency)}${Math.round((combo.fixed_price_cents ?? 0) / 100).toLocaleString()} package`;
    case "buy_x_pay_y":
      return `Book ${combo.buy_qty} · pay ${combo.pay_qty}`;
    default:
      return "Bundle";
  }
}

/** Human line for the combo's booking conditions; null when unconditioned. */
export function comboConditionsLabel(combo: WidgetCombo): string | null {
  const parts: string[] = [];
  if (combo.days_of_week.length > 0 && combo.days_of_week.length < 7) {
    parts.push([...combo.days_of_week].sort().map((d) => DOW[d] ?? "").join("/"));
  }
  if (combo.min_nights) parts.push(`min ${combo.min_nights} nights`);
  if (combo.min_party_size && combo.max_party_size) {
    parts.push(
      combo.min_party_size === combo.max_party_size
        ? `${combo.min_party_size} guests`
        : `${combo.min_party_size}–${combo.max_party_size} guests`,
    );
  } else if (combo.min_party_size) {
    parts.push(`${combo.min_party_size}+ guests`);
  } else if (combo.max_party_size) {
    parts.push(`up to ${combo.max_party_size} guests`);
  }
  // The sales window is enforced against the ARRIVAL date, not the booking
  // date, so "until 31 Aug" was wrong in the way that costs a sale: a guest
  // reads it as a deadline to book by. It bounds which STAYS qualify. Now that
  // future offers are listed rather than hidden, saying so is load-bearing.
  const stayFrom = combo.sales_starts_on ? shortDate(combo.sales_starts_on) : null;
  const stayTo = combo.sales_ends_on ? shortDate(combo.sales_ends_on) : null;
  if (stayFrom && stayTo) parts.push(`for stays ${stayFrom}–${stayTo}`);
  else if (stayFrom) parts.push(`for stays from ${stayFrom}`);
  else if (stayTo) parts.push(`for stays up to ${stayTo}`);
  return parts.length ? parts.join(" · ") : null;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-ZA", {
    day: "numeric",
    month: "short",
  });
}

function stripTrailingZeros(n: number): string {
  return String(Number(n));
}

function currencySymbol(code: string | null): string {
  switch (code) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "ZAR":
    default:
      return "R ";
  }
}

// ─── Offers that apply themselves ────────────────────────────────────────
// The cart asks the server whether it qualifies for anything. Qualification
// and pricing are decided entirely in evaluate_cart_combos, which runs the
// same resolve_combo_price the configured path runs — nothing here decides a
// price, and nothing here should ever start.

export type CartComboEvaluation = {
  ok: boolean;
  applied?: boolean;
  reason?: string;
  combo_id?: string;
  combo_name?: string;
  discount_cents?: number;
  base_total_cents?: number;
  bundle_total_cents?: number;
  /** Per-leg amounts the server wrote. The cart renders these; it never
   *  derives a split of its own. */
  allocations?: Array<{ ref: string; amount_cents: number }>;
};

/**
 * Attach the best qualifying offer to this cart, or preview whether one would.
 *
 * `apply: false` writes nothing — that is the what-if behind "you would lose
 * this discount", asked before an edit rather than after.
 * `override` proposes a change to ONE line ({ booking_id, starts_at, ends_at,
 * party_size } or { booking_id, removed: true }) so the answer is about the
 * edit the guest is actually making.
 */
export async function evaluateCartCombos(
  cartToken: string,
  opts?: {
    apply?: boolean;
    override?: {
      booking_id: string;
      starts_at?: string;
      ends_at?: string;
      party_size?: number;
      removed?: boolean;
    };
  },
): Promise<CartComboEvaluation> {
  const { data, error } = await supabase.rpc("evaluate_cart_combos", {
    p_cart_token: cartToken,
    p_apply: opts?.apply ?? true,
    p_override: opts?.override ?? null,
  });
  // A failure here must never block the cart. The guest simply does not get an
  // offer they were never promised; the alternative is a checkout that breaks
  // because a discount lookup timed out.
  if (error || !data) return { ok: false, reason: error?.message ?? "network_error" };
  return data as CartComboEvaluation;
}

/** Take an auto-applied offer off, restoring each leg's standalone price. */
export async function detachCartCombo(
  cartToken: string,
  comboId: string,
): Promise<{ ok: boolean; reason?: string; restored_lines?: number }> {
  const { data, error } = await supabase.rpc("detach_cart_combo", {
    p_cart_token: cartToken,
    p_combo_id: comboId,
  });
  if (error || !data) return { ok: false, reason: error?.message ?? "network_error" };
  return data as { ok: boolean; reason?: string; restored_lines?: number };
}

export type ComboLineEditResult = {
  ok: boolean;
  reason?: string;
  /** The offer survived the edit. */
  kept_offer?: boolean;
  offer_name?: string;
  previous_discount_cents?: number;
  discount_cents?: number;
  result?: CartComboEvaluation;
};

/**
 * Change or remove a cart line that an auto-applied offer is attached to.
 *
 * One server transaction: detach, reprice, re-evaluate. Not three calls —
 * between the detach and the reprice the guest owes MORE and the offer is
 * already released, so a dropped connection there would leave them at full
 * price with the discount gone.
 *
 * Called with `confirmed: false` it changes nothing and answers
 * `would_lose_offer` when the edit costs them the discount. That round trip
 * genuinely performs the work and rolls it back, because
 * evaluate_cart_combos short-circuits on `already_applied` and will not
 * evaluate an override while an offer is attached.
 */
export async function editCartComboLine(input: {
  cartToken: string;
  bookingId: string;
  startsAt?: string;
  endsAt?: string;
  partySize?: number;
  remove?: boolean;
  confirmed?: boolean;
}): Promise<ComboLineEditResult> {
  const { data, error } = await supabase.rpc("edit_cart_combo_line", {
    p_cart_token: input.cartToken,
    p_booking_id: input.bookingId,
    p_starts_at: input.startsAt ?? null,
    p_ends_at: input.endsAt ?? null,
    p_party_size: input.partySize ?? null,
    p_remove: input.remove ?? false,
    p_confirmed: input.confirmed ?? false,
  });
  if (error || !data) return { ok: false, reason: error?.message ?? "network_error" };
  return data as ComboLineEditResult;
}
